// DataExtractor.ts
import { GET_ACTIONS } from 'data/actions';
import { ExportFormat, ExportTimeRangeOptions, ExportTypes, ScoreBoardExportSections } from 'types/export';
import { Client, gql } from 'urql';
import { getLast30Days, getLast7Days, getLastYear } from './timePeriods';
import { Action } from 'data/actions/types';
import { GET_OBJECTIVES } from 'data/objectives';
import { Objective } from 'data/objectives/types';
import { GET_MEASURABLES } from 'data/measurables';

interface CustomTimeRange {
  startDate: Date;
  endDate: Date;
}

class DataExtractor {
  private client: Client;
  private static instance: DataExtractor;

  // Make the constructor private to prevent direct construction calls with the `new` operator.
  private constructor(client: Client) {
    this.client = client;
  }

  // Provides the global point of access to the instance and ensures only one instance is created.
  public static getInstance(client: Client): DataExtractor {
    if (!DataExtractor.instance) {
      DataExtractor.instance = new DataExtractor(client);
    }
    return DataExtractor.instance;
  }

  async extractData(
    timeRange: ExportTimeRangeOptions,
    exportType: ExportTypes,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    switch (exportType) {
      case ExportTypes.Actions:
        return this.getActions(timeRange, teamId, companyId, customTimeRange);
      case ExportTypes.Objectives:
        return this.getObjectives(timeRange, teamId, companyId, customTimeRange);
      default:
        throw new Error('Unsupported data type');
    }
  }

  public async extractDataForMultipleSections(
    comapanyId: number,
    teamId: number,
    exportType: ExportTypes,
    timeRange: ExportTimeRangeOptions,
    customTimeRange?: CustomTimeRange,
    sections?: ScoreBoardExportSections,
  ) {
    switch (exportType) {
      case ExportTypes.Scoreboard:
        return this.extractMultipleScoreboardSections(comapanyId, teamId, timeRange, customTimeRange, sections);
        break;
      default:
        throw new Error('Unsupported export type for sections extraction.');
    }
  }

  private async extractMultipleScoreboardSections(
    companyId: number,
    teamId: number,
    timeRange: ExportTimeRangeOptions,
    customTimeRange?: CustomTimeRange,
    sections?: ScoreBoardExportSections,
  ) {
    if (!sections) {
      throw new Error('Sections to be extracted are required.');
    }

    const timeRanges = this.obtainTimeRanges(timeRange, 'date', customTimeRange);
    const where = {
      ...timeRanges,
      company_id: { _eq: companyId },
      teams_id: { _eq: teamId },
      archived: { _neq: true },
    };
    const results = await this.client.query(GET_MEASURABLES, { where }).toPromise();

    const measurablesData = results?.data?.measurables || [];
    const groupedMeasurables = measurablesData.reduce((result, measurable) => {
      (result[measurable['interval']] = result[measurable['interval']] || []).push(measurable);
      return result;
    }, {});

    console.log({ groupedMeasurables });
    console.log(groupedMeasurables.weekly);
  }

  private async getActions(
    timeRange: ExportTimeRangeOptions,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    // Logic to determine the 'where' condition based on timeRange
    const timeRanges = this.obtainTimeRanges(timeRange, 'date', customTimeRange);
    const where = { ...timeRanges, company_id: { _eq: companyId }, teams_id: { _eq: teamId } };
    const result = await this.client.query(GET_ACTIONS, { where }).toPromise();
    const cleanedActions = await this.cleanActionData(result?.data?.actions || []);
    return cleanedActions;
  }

  private async getObjectives(
    timeRange: ExportTimeRangeOptions,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    // Similar to getActions, implement querying for objectives
    const timeRanges = this.obtainTimeRanges(timeRange, 'created_at', customTimeRange);
    const where = { ...timeRanges, company_id: { _eq: companyId }, teams_id: { _eq: teamId } };
    const result = await this.client.query(GET_OBJECTIVES, { where }).toPromise();
    const cleanedObjectives = this.cleanObjectivesData(result?.data?.objectives) || [];
    return cleanedObjectives;
  }

  private obtainTimeRanges = (
    timeRange: ExportTimeRangeOptions,
    propertyValue: string,
    customDates?: CustomTimeRange,
  ) => {
    let clause;
    switch (timeRange) {
      case ExportTimeRangeOptions.LastSevenDays:
        const { startDate: start7, endDate: end7 } = getLast7Days();
        clause = { [propertyValue]: { _gte: start7.toISOString(), _lte: end7.toISOString() } };
        return clause;
      case ExportTimeRangeOptions.LastThirtyDays:
        const { startDate: start30, endDate: end30 } = getLast30Days();
        clause = { [propertyValue]: { _gte: start30.toISOString(), _lte: end30.toISOString() } };
        return clause;
      case ExportTimeRangeOptions.LastYear:
        const { startDate: startY, endDate: endY } = getLastYear();
        clause = { [propertyValue]: { _gte: startY.toISOString(), _lte: endY.toISOString() } };
        return clause;
      case ExportTimeRangeOptions.All:
        clause = {};
        return clause;
      case ExportTimeRangeOptions.Custom:
        console.log({ customDates });
        clause = {};
        clause = {
          [propertyValue]: { _gte: customDates.startDate.toISOString(), _lte: customDates.endDate.toISOString() },
        };
        return clause;
      default:
        throw new Error('Unsupported time range');
    }
  };

  private cleanActionData(actions: Action[]) {
    return actions.map((action) => {
      try {
        // Assuming the description is a JSON string that needs parsing
        const descriptionObj = JSON.parse(action.description || '{}');
        const blocks = descriptionObj.blocks || [];
        const firstBlock = blocks[0] || {};
        const text = firstBlock.text || '';
        let attachmenthsString = '';

        if (action.action_files && action.action_files.length > 0) {
          action.action_files.forEach((a, index) => {
            if (index >= 1) {
              attachmenthsString += ` | ${a.url}`;
            } else {
              attachmenthsString += a.url;
            }
          });
        }

        let commentString = '';
        if (Array.isArray(action.action_comments)) {
          action.action_comments.map((com) => {
            commentString += this.extractTextFromAttribute(com, 'comment');
            commentString += ` ${new Date(com.timestamp).toLocaleString()} |`;
          });
        }

        return {
          ...action,
          description: text, // Update the description with the extracted text
          attachments: attachmenthsString,
          assignee: action.user?.name,
          action_comments: commentString,
        };
      } catch (error) {
        console.error('Error cleaning action:', error);
        return action; // Return the action unchanged in case of parsing error
      }
    });
  }

  private cleanObjectivesData(objectives: Objective[]): Objective[] {
    return objectives?.map((objective) => {
      try {
        return {
          ...objective,
          description: this.extractTextFromAttribute(objective, 'description'),
          milestone30days: this.extractTextFromAttribute(objective, 'column_30_day'),
          milestone60days: this.extractTextFromAttribute(objective, 'column_60_day'),
          keyResults: this.extractTextFromAttribute(objective, 'key_results'),
          owned_user: objective?.user?.name,
        };
      } catch (error) {
        console.error('Error cleaning action:', error);
        return objective; // Return the action unchanged in case of parsing error
      }
    });
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  private extractTextFromAttribute(objective: { [key: string]: any }, attributeName: string): string {
    try {
      // Parse the JSON string from the specified attribute
      const attributeObj = JSON.parse(objective[attributeName] || '{}');
      const blocks = attributeObj.blocks || [];
      const firstBlock = blocks[0] || {};
      const text = firstBlock.text || '';
      return text;
    } catch (error) {
      console.error(`Error extracting text from attribute ${attributeName}:`, error);
      return ''; // Return empty string in case of parsing error
    }
  }
}

export default DataExtractor;
